import { useMemo, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { useAuth } from '@/context/AuthContext';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useCampaignMetrics } from '@/hooks/useCampaignMetrics';
import {
  PLATFORM_LABELS,
  CAMPAIGN_STATUS_LABELS,
  computeCampaignKpis,
  deleteCampaignMetric,
  updateCampaignStatus,
  consolidatedMarketingInvestment,
  isCampaignClosed,
  type Campaign,
  type CampaignMetric,
} from '@/services/campaigns';
import CampaignModal from './components/CampaignModal';
import MetricModal from './components/MetricModal';

interface RawTotals {
  investment: number;
  conversations: number;
  sales: number;
  revenue: number;
}

function fmtMoney(v: number): string {
  return `$${Math.round(v).toLocaleString('es-CO')}`;
}
function fmtCpa(v: number): string {
  return v > 0 ? fmtMoney(v) : '—';
}
function fmtRoas(v: number): string {
  return v > 0 ? `${v.toFixed(2)}x` : '—';
}
function fmtPct(v: number): string {
  return v > 0 ? `${v.toFixed(1)}%` : '—';
}
function fmtDate(value: string): string {
  if (!value) return '—';
  return new Date(`${value}T00:00:00`).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
function sumRows(rows: RawTotals[]): RawTotals {
  return rows.reduce(
    (acc, r) => ({
      investment: acc.investment + r.investment,
      conversations: acc.conversations + r.conversations,
      sales: acc.sales + r.sales,
      revenue: acc.revenue + r.revenue,
    }),
    { investment: 0, conversations: 0, sales: 0, revenue: 0 },
  );
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  marketing: 'Marketing',
  accountant: 'Contabilidad',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'text-emerald-700 bg-emerald-50',
  paused: 'text-amber-700 bg-amber-50',
  finished: 'text-foreground-600 bg-background-100',
};

export default function CampaignsPage() {
  const { profile } = useAuth();
  const role = profile?.role ?? '';
  const canWrite = role === 'admin' || Boolean(profile?.permissions?.campaigns?.write);
  const canDelete = role === 'admin' || Boolean(profile?.permissions?.campaigns?.delete);
  const readOnly = !canWrite;

  const { data: campaigns, loading, error: campaignsError, refetch } = useCampaigns();
  const { data: metrics, loading: metricsLoading, error: metricsError, refetch: refetchMetrics } =
    useCampaignMetrics();

  const [activeId, setActiveId] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [modal, setModal] = useState<{ editing: CampaignMetric | null } | null>(null);
  const [busyRow, setBusyRow] = useState('');
  const [rowError, setRowError] = useState('');
  const [statusModal, setStatusModal] = useState<{ status: 'active' | 'paused' | 'finished' } | null>(
    null,
  );
  const [declaredCostInput, setDeclaredCostInput] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState('');

  const active: Campaign | undefined = campaigns.find((c) => c.id === activeId) ?? campaigns[0];

  const activeMetrics = useMemo(
    () => metrics.filter((m) => m.campaignId === active?.id),
    [metrics, active],
  );

  // Panel operativo: la inversión operativa sale SOLO de los registros diarios.
  const activeRaw = useMemo<RawTotals>(() => sumRows(activeMetrics), [activeMetrics]);
  const activeKpis = computeCampaignKpis(activeRaw);

  const globalRaw = useMemo<RawTotals>(() => sumRows(metrics), [metrics]);
  const globalKpis = computeCampaignKpis(globalRaw);
  // Gasto consolidado global: solo campañas pausadas/finalizadas (declared_cost).
  const consolidated = useMemo(() => consolidatedMarketingInvestment(campaigns), [campaigns]);

  async function handleDeleteMetric(id: string) {
    setRowError('');
    setBusyRow(id);
    const { error } = await deleteCampaignMetric(id);
    setBusyRow('');
    if (error) {
      setRowError(error);
      return;
    }
    refetchMetrics();
  }

  function openStatus(next: 'active' | 'paused' | 'finished') {
    setStatusError('');
    if (next === 'active') {
      void applyStatus('active');
      return;
    }
    setDeclaredCostInput(active?.declaredCost ? String(active.declaredCost) : '');
    setStatusModal({ status: next });
  }

  async function applyStatus(next: 'active' | 'paused' | 'finished', declaredCost?: number) {
    if (!active) return;
    setSavingStatus(true);
    setStatusError('');
    const { error } = await updateCampaignStatus(active.id, next, declaredCost);
    setSavingStatus(false);
    if (error) {
      setStatusError(error);
      return;
    }
    setStatusModal(null);
    refetch();
  }

  const loadingAll = loading || metricsLoading;
  const loadError = campaignsError || metricsError;

  function retryAll() {
    refetch();
    refetchMetrics();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {loadError && <ErrorState message={loadError} onRetry={retryAll} />}
        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Marketing y Campañas</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>Panel operativo de seguimiento de campañas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap">
                {ROLE_LABELS[role] ?? role ?? 'Sin rol'}
              </span>
            </p>
          </div>
          {canWrite && (
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nueva Campaña
            </button>
          )}
        </div>

        {readOnly && (
          <div className="flex items-center gap-2.5 bg-secondary-50 border border-secondary-200 rounded-xl px-4 py-3">
            <i className="ri-eye-line text-secondary-700"></i>
            <p className="text-sm text-secondary-900">
              Estás en <strong>modo solo lectura</strong>. Puedes consultar la evolución de las campañas, pero no editar los valores de entrada.
            </p>
          </div>
        )}

        {/* KPIs globales */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-lock-2-line text-accent-500"></i>Gasto consolidado (cerradas)
            </div>
            <p className="text-xl font-bold text-foreground-950 mt-2">{fmtMoney(consolidated)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-wallet-3-line text-accent-500"></i>Inversión operativa (registros)
            </div>
            <p className="text-xl font-bold text-foreground-950 mt-2">{fmtMoney(globalRaw.investment)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-shopping-bag-3-line text-accent-500"></i>Facturación
            </div>
            <p className="text-xl font-bold text-foreground-950 mt-2">{fmtMoney(globalRaw.revenue)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-rocket-2-line text-accent-500"></i>ROAS
            </div>
            <p className="text-xl font-bold text-accent-600 mt-2">{fmtRoas(globalKpis.roas)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-focus-3-line text-accent-500"></i>CPA
            </div>
            <p className="text-xl font-bold text-foreground-950 mt-2">{fmtCpa(globalKpis.cpa)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-percent-line text-accent-500"></i>% Cierre
            </div>
            <p className="text-xl font-bold text-foreground-950 mt-2">{fmtPct(globalKpis.conversion)}</p>
          </div>
        </div>

        {loadingAll ? (
          <div className="bg-white rounded-xl border border-background-200 py-16 text-center text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando campañas...
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white rounded-xl border border-background-200 py-16 text-center">
            <i className="ri-megaphone-line text-4xl text-foreground-300 block mb-3"></i>
            <p className="text-foreground-600 text-sm mb-1">Aún no hay campañas registradas.</p>
            <p className="text-xs text-foreground-400 mb-4">Crea una campaña para empezar a registrar su evolución diaria.</p>
            {canWrite && (
              <button
                onClick={() => setShowNew(true)}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
              >
                <i className="ri-add-line"></i>Nueva Campaña
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Selector de campaña */}
            <div className="flex flex-wrap gap-1.5">
              {campaigns.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    active?.id === c.id
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {c.campaignName}
                </button>
              ))}
            </div>

            {active && (
              <div className="space-y-4">
                {/* Encabezado de la campaña activa */}
                <div className="bg-white rounded-xl border border-background-200 p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-megaphone-line text-accent-600 text-lg"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="text-base font-semibold text-foreground-950">{active.campaignName}</h2>
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap text-foreground-700 bg-background-100">
                            {PLATFORM_LABELS[active.platform] ?? active.platform}
                          </span>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_BADGE[active.status] ?? STATUS_BADGE.active}`}>
                            {CAMPAIGN_STATUS_LABELS[active.status] ?? active.status}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {active.adsetName || 'Sin conjunto'}
                          {active.adName ? ` · ${active.adName}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canWrite && active.status !== 'active' && (
                        <button
                          onClick={() => openStatus('active')}
                          disabled={savingStatus}
                          className="text-sm px-3.5 py-2.5 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60"
                        >
                          <i className="ri-play-line mr-1"></i>Activar
                        </button>
                      )}
                      {canWrite && active.status !== 'paused' && (
                        <button
                          onClick={() => openStatus('paused')}
                          className="text-sm px-3.5 py-2.5 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors whitespace-nowrap font-medium cursor-pointer"
                        >
                          <i className="ri-pause-line mr-1"></i>Pausar
                        </button>
                      )}
                      {canWrite && active.status !== 'finished' && (
                        <button
                          onClick={() => openStatus('finished')}
                          className="text-sm px-3.5 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
                        >
                          <i className="ri-flag-2-line mr-1"></i>Finalizar
                        </button>
                      )}
                      {canWrite && (
                        <button
                          onClick={() => setModal({ editing: null })}
                          className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
                        >
                          <i className="ri-add-line mr-1.5"></i>Registrar datos
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Presupuesto / costo declarado */}
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-secondary-50 rounded-lg p-3">
                      <p className="text-xs text-foreground-500">Presupuesto tope (solo control)</p>
                      <p className="text-base font-semibold text-foreground-950 mt-1">
                        {active.budgetLimit > 0 ? fmtMoney(active.budgetLimit) : '—'}
                      </p>
                    </div>
                    <div className="bg-secondary-50 rounded-lg p-3">
                      <p className="text-xs text-foreground-500">
                        {isCampaignClosed(active.status) ? 'Inversión declarada (consolida)' : 'Inversión declarada'}
                      </p>
                      <p className="text-base font-semibold text-foreground-950 mt-1">
                        {active.declaredCost > 0 ? fmtMoney(active.declaredCost) : '—'}
                      </p>
                    </div>
                  </div>

                  {/* KPI acumulados de la campaña (operativos) */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                    <div className="bg-background-50 rounded-lg p-3">
                      <p className="text-xs text-foreground-500">Inversión (registros)</p>
                      <p className="text-lg font-bold text-foreground-950 mt-1">{fmtMoney(activeRaw.investment)}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <p className="text-xs text-foreground-500">Conversaciones</p>
                      <p className="text-lg font-bold text-foreground-950 mt-1">{activeRaw.conversations.toLocaleString('es-CO')}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <p className="text-xs text-foreground-500">Ventas</p>
                      <p className="text-lg font-bold text-foreground-950 mt-1">{activeRaw.sales.toLocaleString('es-CO')}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-3">
                      <p className="text-xs text-foreground-500">Facturación</p>
                      <p className="text-lg font-bold text-foreground-950 mt-1">{fmtMoney(activeRaw.revenue)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="bg-accent-50 border border-accent-100 rounded-lg p-3 text-center">
                      <p className="text-xs text-accent-700">CPA</p>
                      <p className="text-base font-bold text-foreground-950 mt-1">{fmtCpa(activeKpis.cpa)}</p>
                    </div>
                    <div className="bg-accent-50 border border-accent-100 rounded-lg p-3 text-center">
                      <p className="text-xs text-accent-700">ROAS</p>
                      <p className="text-base font-bold text-foreground-950 mt-1">{fmtRoas(activeKpis.roas)}</p>
                    </div>
                    <div className="bg-accent-50 border border-accent-100 rounded-lg p-3 text-center">
                      <p className="text-xs text-accent-700">% Cierre</p>
                      <p className="text-base font-bold text-foreground-950 mt-1">{fmtPct(activeKpis.conversion)}</p>
                    </div>
                  </div>
                </div>

                {/* Tabla de registros diarios */}
                {rowError && (
                  <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                    <i className="ri-error-warning-line"></i>
                    <span>{rowError}</span>
                  </div>
                )}

                <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-100 text-left bg-background-50">
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">Fecha</th>
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">Inversión</th>
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">Conversaciones</th>
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">Ventas</th>
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">Facturación</th>
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">CPA</th>
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">ROAS</th>
                          <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">% Cierre</th>
                          {(canWrite || canDelete) && (
                            <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right whitespace-nowrap">Acciones</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {activeMetrics.map((m) => {
                          const k = computeCampaignKpis(m);
                          return (
                            <tr key={m.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                              <td className="px-5 py-3 text-foreground-800 whitespace-nowrap">
                                {fmtDate(m.entryDate)}
                                {m.notes && <p className="text-xs text-foreground-400 max-w-[180px] truncate">{m.notes}</p>}
                              </td>
                              <td className="px-5 py-3 text-right text-foreground-800 whitespace-nowrap">{fmtMoney(m.investment)}</td>
                              <td className="px-5 py-3 text-right text-foreground-800 whitespace-nowrap">{m.conversations.toLocaleString('es-CO')}</td>
                              <td className="px-5 py-3 text-right text-foreground-800 whitespace-nowrap">{m.sales.toLocaleString('es-CO')}</td>
                              <td className="px-5 py-3 text-right font-semibold text-foreground-950 whitespace-nowrap">{fmtMoney(m.revenue)}</td>
                              <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">{fmtCpa(k.cpa)}</td>
                              <td className="px-5 py-3 text-right text-accent-600 font-medium whitespace-nowrap">{fmtRoas(k.roas)}</td>
                              <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">{fmtPct(k.conversion)}</td>
                              {(canWrite || canDelete) && (
                                <td className="px-5 py-3 text-right whitespace-nowrap">
                                  <div className="flex items-center justify-end gap-1">
                                    {canWrite && (
                                      <button
                                        onClick={() => setModal({ editing: m })}
                                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                                        aria-label="Editar"
                                      >
                                        <i className="ri-pencil-line"></i>
                                      </button>
                                    )}
                                    {canDelete && (
                                      <button
                                        onClick={() => handleDeleteMetric(m.id)}
                                        disabled={busyRow === m.id}
                                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"
                                        aria-label="Eliminar"
                                      >
                                        <i className={busyRow === m.id ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'}></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {activeMetrics.length === 0 && (
                    <div className="text-center py-14 text-foreground-500">
                      <i className="ri-line-chart-line text-3xl block mb-2"></i>
                      <p className="text-sm">Aún no hay registros para esta campaña.</p>
                      {canWrite && (
                        <p className="text-xs text-foreground-400 mt-1">
                          Usa <strong>Registrar datos</strong> para añadir la evolución diaria.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showNew && (
        <CampaignModal
          onClose={() => setShowNew(false)}
          onSaved={() => {
            refetch();
          }}
        />
      )}

      {modal && active && (
        <MetricModal
          campaignId={active.id}
          campaignName={active.campaignName}
          editing={modal.editing}
          onClose={() => setModal(null)}
          onSaved={() => refetchMetrics()}
        />
      )}

      {statusModal && active && (
        <Modal
          title={statusModal.status === 'finished' ? 'Finalizar campaña' : 'Pausar campaña'}
          subtitle={`${active.campaignName} · registra el costo total invertido`}
          onClose={() => setStatusModal(null)}
          maxWidth="max-w-md"
        >
          <div className="space-y-4">
            {statusError && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line"></i>
                <span>{statusError}</span>
              </div>
            )}
            <p className="text-sm text-foreground-600">
              Al {statusModal.status === 'finished' ? 'finalizar' : 'pausar'} la campaña, el
              <strong> costo total invertido declarado</strong> se consolida en los reportes globales.
              El presupuesto tope no se suma.
            </p>
            <div>
              <label className={labelCls}>Costo total invertido declarado ($)</label>
              <input
                type="number"
                min="0"
                className={inputCls}
                value={declaredCostInput}
                onChange={(e) => setDeclaredCostInput(e.target.value)}
                placeholder="Ej: 250000"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setStatusModal(null)}
                className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => applyStatus(statusModal.status, Number(declaredCostInput) || 0)}
                disabled={savingStatus}
                className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingStatus ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
                  </>
                ) : (
                  <>Confirmar</>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}